import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import GamifiedButton from '../components/common/GamifiedButton';
import { useLang } from '../i18n/LanguageContext';
import SecretDesParents from '../components/SecretDesParents';

/**
 * Règles du jeu — page publique (/regles-de-jeu).
 *
 * C'est la cible du QR code imprimé au dos de la boîte : un acheteur la scanne
 * pour retrouver les règles complètes. Deux conséquences de design :
 *  - la page peut être ouverte EN PLEINE FÊTE, y compris par un invité, donc
 *    la réponse de la question subsidiaire est masquée derrière un clic ;
 *  - elle doit rester lisible et imprimable sur mobile.
 *
 * Source : « [Logo de Mirum Games] - Présentation.pdf » fourni par Mirum Games.
 *
 * i18n : les textes viennent du fragment src/i18n/fr/regles.js. SEULE exception
 * volontaire, la RÉPONSE à la question subsidiaire reste en dur plus bas — elle
 * ne doit exister dans aucun dictionnaire traduit (voir l'en-tête du fragment).
 * Les deux listes ci-dessous sont donc construites à l'appel, pas au chargement
 * du module : elles doivent se recalculer quand le visiteur change de langue.
 */

// La réponse de la question subsidiaire reste EN DUR, ici et nulle part
// ailleurs : lui donner une clé i18n la publierait dans tous les dictionnaires
// traduits. Seule l'unité change de langue — le nombre est celui du verso
// imprimé du questionnaire.
const REPONSE_SUBSIDIAIRE = {
  fr: '1980 couches',
  en: '1,980 nappies',
  nl: '1980 luiers',
  de: '1980 Windeln',
  es: '1980 pañales',
  lb: '1980 Wëndelen',
  pt: '1980 fraldas',
};

const buildQuestions = (t) => [
  t('regles.questions.q1'),
  t('regles.questions.q2'),
  t('regles.questions.q3'),
  t('regles.questions.q4'),
  t('regles.questions.q5'),
  t('regles.questions.q6'),
  t('regles.questions.q7'),
  t('regles.questions.q8'),
  t('regles.questions.q9'),
  t('regles.questions.q10'),
  t('regles.questions.q11'),
];

const buildSteps = (t) => [
  {
    n: 1,
    title: t('regles.etape1.titre'),
    photo: '/photos/contenu.webp',
    alt: t('regles.etape1.alt'),
    body: [
      t('regles.etape1.p1'),
      t('regles.etape1.p2'),
      t('regles.etape1.p3'),
    ],
  },
  {
    n: 2,
    title: t('regles.etape2.titre'),
    photo: '/photos/pochons.webp',
    alt: t('regles.etape2.alt'),
    body: [
      t('regles.etape2.p1'),
    ],
    teams: [
      { label: t('regles.etape2.equipe1Label'), text: t('regles.etape2.equipe1Texte') },
      { label: t('regles.etape2.equipe2Label'), text: t('regles.etape2.equipe2Texte') },
    ],
  },
  {
    n: 3,
    title: t('regles.etape3.titre'),
    photo: '/photos/boite-verso.webp',
    alt: t('regles.etape3.alt'),
    body: [
      t('regles.etape3.p1'),
      t('regles.etape3.p2'),
      t('regles.etape3.p3'),
    ],
  },
  {
    n: 4,
    title: t('regles.etape4.titre'),
    photo: '/photos/boite-recto.webp',
    alt: t('regles.etape4.alt'),
    body: [
      t('regles.etape4.p1'),
      t('regles.etape4.p2'),
    ],
  },
];

export default function GameRules() {
  const [showAnswer, setShowAnswer] = useState(false);
  const { t, lang } = useLang();

  const QUESTIONS = buildQuestions(t);
  const STEPS = buildSteps(t);

  return (
    <div className="bg-[#F7F4EE]">
      <SEO
        title={t('regles.seo.titre')}
        description={t('regles.seo.description')}
        url="/regles-de-jeu"
        keywords="règles jeu gender reveal, comment jouer joyful reveal, jeu révélation sexe bébé règles, gender reveal party jeu"
        image="/photos/boite-recto.webp"
      />

      {/* En-tête */}
      <section className="px-5 pt-14 pb-10 text-center bg-gradient-to-b from-white to-[#F7F4EE]">
        <div className="max-w-[820px] mx-auto">
          <p className="m-0 mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#4AA294]">
            Joyful Reveal
          </p>
          <h1 className="m-0 mb-4 text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#33403B]">
            {t('regles.entete.titre')}
          </h1>
          <p className="m-0 text-base leading-relaxed text-[#33403B]/80">
            {t('regles.entete.introAvant')} <strong>{t('regles.entete.introDuree')}</strong>
            {t('regles.entete.introEntre')} <strong>{t('regles.entete.introJoueurs')}</strong>.
          </p>
        </div>
      </section>

      {/* Avertissement invités — la page est la cible du QR code de la boîte */}
      <section className="px-5 pb-10">
        <div className="max-w-[820px] mx-auto rounded-xl border-2 border-[#EDB82F] bg-[#FFF8E6] p-5 sm:p-4">
          <p className="m-0 text-[15px] leading-relaxed text-[#33403B]">
            <strong>{t('regles.invite.titre')}</strong> {t('regles.invite.destinee')}
            <strong> {t('regles.invite.parents')}</strong> {t('regles.invite.suite')}
          </p>
        </div>
      </section>

      {/* Contenu du kit */}
      <section className="px-5 pb-12">
        <div className="max-w-[820px] mx-auto rounded-2xl bg-white p-5 sm:p-7 shadow-sm border-2 border-[#4AA294]/20">
          <h2 className="m-0 mb-4 text-xl font-black uppercase tracking-tight text-[#33403B]">
            {t('regles.kit.titre')}
          </h2>
          <ul className="m-0 pl-5 space-y-2 text-[15px] leading-relaxed text-[#33403B]">
            <li>
              {t('regles.kit.sachetAvant')} <strong>{t('regles.kit.sachetPieces')}</strong>,
            </li>
            <li>
              {t('regles.kit.grattoirAvant')} <strong>{t('regles.kit.grattoirPiece')}</strong>{' '}
              {t('regles.kit.grattoirFin')}
            </li>
          </ul>
          <p className="mt-4 mb-0 text-sm italic text-[#2F7E72]">
            {t('regles.kit.premium')}
          </p>
        </div>
      </section>

      {/* Les étapes */}
      <section className="px-5 pb-4">
        <div className="max-w-[980px] mx-auto space-y-10">
          {STEPS.map((s) => (
            <article
              key={s.n}
              className="grid gap-6 md:grid-cols-[260px_1fr] items-start rounded-2xl bg-white p-5 sm:p-7 shadow-sm border-2 border-[#4AA294]/20"
            >
              {/* 90vw annonçait 337 px sur un téléphone de 375 px, alors que
                  l'emplacement réel mesure 291 px : le navigateur choisissait
                  la variante 1200 px pour les quatre photos, soit 945 ko de
                  photos au lieu de 260. */}
              <img
                src={s.photo}
                srcSet={`${s.photo.replace('.webp', '-600.webp')} 600w, ${s.photo} 1200w`}
                sizes="(max-width: 768px) 78vw, 260px"
                alt={s.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-[220px] object-cover rounded-xl border-2 border-[#4AA294]/25"
              />
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#EDB82F] to-[#E1A72E] text-base font-black text-white">
                    {s.n}
                  </span>
                  <h2 className="m-0 text-lg sm:text-xl font-black uppercase tracking-tight text-[#33403B]">
                    {s.title}
                  </h2>
                </div>

                {s.body.map((p, i) => (
                  <p key={i} className="m-0 mb-3 text-[15px] leading-relaxed text-[#33403B]">
                    {p}
                  </p>
                ))}

                {s.teams && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    {/* `equipe` et non `t` : `t` est la fonction de traduction. */}
                    {s.teams.map((equipe) => (
                      <div
                        key={equipe.label}
                        className="rounded-xl border-2 border-[#4AA294]/30 bg-[#F7F4EE] p-4 text-center"
                      >
                        <span className="block text-sm font-black uppercase tracking-wide text-[#2F7E72]">
                          {equipe.label}
                        </span>
                        <span className="block mt-1 text-sm text-[#33403B]">{equipe.text}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Question subsidiaire — réponse masquée : un invité peut lire cette page */}
                {s.n === 4 && (
                  <div className="mt-5 rounded-xl border-2 border-[#4AA294]/30 bg-[#F7F4EE] p-5">
                    <p className="m-0 mb-3 text-xs font-bold uppercase tracking-wide text-[#4AA294]">
                      {t('regles.subsidiaire.surtitre')}
                    </p>
                    <p className="m-0 mb-4 text-[15px] font-bold leading-relaxed text-[#33403B]">
                      {t('regles.subsidiaire.question')}
                    </p>

                    {showAnswer ? (
                      <p className="m-0 rounded-lg bg-white px-4 py-3 text-[15px] text-[#33403B] border-2 border-[#EDB82F]">
                        {t('regles.subsidiaire.reponseLabel')}{' '}
                        <strong>{REPONSE_SUBSIDIAIRE[lang] || REPONSE_SUBSIDIAIRE.fr}</strong>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowAnswer(true)}
                        data-cta="regles:voir-reponse"
                        className="rounded-lg bg-gradient-to-br from-[#4AA294] to-[#2F7E72] px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F]"
                      >
                        {t('regles.subsidiaire.bouton')}
                      </button>
                    )}

                    {/* Elle est proposée, pas imposée : chacun peut inventer la sienne */}
                    <p className="m-0 mt-3 text-[13px] italic leading-relaxed text-[#33403B]/70">
                      {t('regles.subsidiaire.libre')}
                    </p>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Le conseil */}
      <section className="px-5 py-12">
        <div className="max-w-[820px] mx-auto rounded-2xl bg-gradient-to-br from-[#2F7E72] to-[#4AA294] p-6 sm:p-8 text-center text-white shadow-lg">
          <p className="m-0 mb-2 text-sm font-bold uppercase tracking-[0.15em] text-[#EDB82F]">
            {t('regles.conseil.surtitre')}
          </p>
          <h2 className="m-0 mb-4 text-xl sm:text-2xl font-black uppercase tracking-tight">
            {t('regles.conseil.titre')}
          </h2>
          <p className="m-0 text-[15px] leading-relaxed text-white/90">
            {t('regles.conseil.texte')}
          </p>
        </div>
      </section>

      {/* Le secret des parents — replié : cette page est la cible du QR code
          de la boîte, un invité peut la lire pendant la partie. */}
      <section className="px-5 pb-12">
        <SecretDesParents masque className="mx-auto max-w-[820px]" />
      </section>

      {/* Les 11 questions */}
      <section className="px-5 pb-12">
        <div className="max-w-[820px] mx-auto rounded-2xl bg-white p-5 sm:p-7 shadow-sm border-2 border-[#4AA294]/20">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="m-0 text-xl font-black uppercase tracking-tight text-[#33403B]">
              {t('regles.questions.titre')}
            </h2>
            <button
              type="button"
              onClick={() => window.print()}
              data-cta="regles:imprimer"
              className="rounded-lg border-2 border-[#4AA294] px-4 py-2 text-sm font-bold text-[#2F7E72] transition-all hover:bg-[#4AA294] hover:text-white"
            >
              {t('regles.questions.imprimer')}
            </button>
          </div>

          <ol className="m-0 pl-5 space-y-3 text-[15px] leading-relaxed text-[#33403B]">
            {QUESTIONS.map((q, i) => (
              <li key={i} className="pl-1">{q}</li>
            ))}
          </ol>

          <p className="mt-6 mb-0 text-sm leading-relaxed text-[#33403B]/75">
            {t('regles.questions.persoAvant')} <strong>{t('regles.questions.persoMaman')}</strong>{' '}
            {t('regles.questions.persoOu')} <strong>{t('regles.questions.persoPapa')}</strong>
            {t('regles.questions.persoFin')}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 pb-16 text-center">
        <div className="max-w-[700px] mx-auto">
          <h2 className="m-0 mb-3 text-xl sm:text-2xl font-black uppercase tracking-tight text-[#33403B]">
            {t('regles.cta.titre')}
          </h2>
          <p className="m-0 mb-6 text-[15px] text-[#33403B]/80">
            {t('regles.cta.texte')}
          </p>
          <Link to="/shop" className="no-underline">
            <GamifiedButton variant="primary" size="lg" data-cta="regles:vers-boutique">
              {t('regles.cta.bouton')}
            </GamifiedButton>
          </Link>
        </div>
      </section>

      <style>{`
        @media print {
          nav, footer, [data-cta] { display: none !important; }
          .bg-\\[\\#F7F4EE\\] { background: #fff !important; }
          article { break-inside: avoid; page-break-inside: avoid; }
          img { max-height: 150px; }
        }
      `}</style>
    </div>
  );
}
